// PenKindPicker — ペン種選択の共有部品 (Modern / Classic 両用).
//
// 背景 (consult 2026-06-20, A 採択): Classic ToolPanel (80px サイドバー) の縦圧解消 +
// Modern パネルの再利用 (DRY)。これまで Modern (`ModernToolProperties`) がインラインで
// 持っていた **ペン種チップ (ブラシ / サインペン / テクスチャ = `MODERN_BRUSH_SUBTOOLS`) +
// テクスチャ preset セクション (`TexturePresetPicker`、pen==='texture' のとき)** を、
// 重複実装を避けてここに切り出し、Modern と Classic の双方から使う。
//
// - Modern: `ModernToolProperties` のパネル先頭 (消しゴム時以外) で inline に置く。
// - Classic: 常設フロート「ペン」(`PenPopup`) の中身として置く。
//
// 本部品は「ペン種チップ + (texture/airbrush のとき) ペン別パラメータ」だけを担う
// 純粋な部品 (= レイアウト非依存)。pen='texture' への切替・penParams 反映は呼び出し側
// (= 共有 chrome の `onSelectPen` / `onSelectTexturePreset`、penParamsFor) の責務で、
// 本部品は「どのペン種 / どの preset・値を選ぶか」だけを表現する (= engine / protocol
// 不変)。
//
// **Modern の見た目・挙動は不変**: 抽出前の `ModernToolProperties` インライン実装の
// chip class / fieldset / TexturePresetPicker 配置をそのまま移設している。
//
// 構成 (modern-brush-panel-2026-08-17.md §8 / §9-1、共有単位の切り直し): 本部品は
// **(a) ペン種チップ列** + **(b) ペン別パラメータ = `PenParamsSection`** の合成。
// (b) を切り出したのは、表現パリティを安く担保しているのは実際には (b) が両レイアウトへ
// 相乗りしていることであって (a) の見た目ではないため。Modern はいずれ (a) を独立の
// 「ブラシ」パネルへ移す予定 (§9-1 後続タスク) だが、Classic は本部品 (a+b) をそのまま
// 使い続ける (§10.3 の常設フロート「ペン」、Classic は現状維持)。

import type { StrokePen, TexturePreset } from '@oekaki/protocol';

import { MODERN_BRUSH_SUBTOOLS } from '../modern/modernTools.js';
import { PenParamsSection } from './PenParamsSection.js';

/** チップ class (抽出前の ModernToolProperties.chipClass と同一)。 */
const chipClass = (active: boolean): string =>
  `whitespace-nowrap rounded border px-2 py-1 text-[11px] font-bold transition-colors ${
    active
      ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-black'
      : 'border-[var(--color-border)] bg-[var(--color-bg-soft)] text-white hover:bg-[var(--color-bg)]'
  }`;

export interface PenKindPickerProps {
  /** 現在のペン種 (= チップのハイライト対象)。 */
  readonly pen: StrokePen;
  /**
   * ペン種チップ選択。`MODERN_BRUSH_SUBTOOLS` の値 (brush/flat/texture/tone/airbrush/
   * crayon/pastel、phase11-light-pens-2026-08-21.md でクレヨン/パステルを追加)。
   */
  readonly onSelectPen: (
    pen: Extract<
      StrokePen,
      'brush' | 'flat' | 'texture' | 'tone' | 'airbrush' | 'crayon' | 'pastel' | 'dot'
    >,
  ) => void;
  /** 現在の texture preset (= `pen==='texture'` のとき preset サムネのハイライト対象)。 */
  readonly texturePreset: TexturePreset;
  /** texture preset サムネクリックで発火。呼び出し側が pen.texturePreset を書く。 */
  readonly onSelectTexturePreset: (preset: TexturePreset) => void;
  /**
   * エアブラシの粒の粗さ (grain, 0..3) — `pen==='airbrush'` のときだけスライダを出す
   * (airbrush-design-2026-08-15.md §1.3 / §6)。`pen.airbrushGrain` を渡す。
   */
  readonly airbrushGrain: number;
  /** grain スライダ変更で発火。呼び出し側が pen.airbrushGrain を書く。 */
  readonly onAirbrushGrainChange: (grain: number) => void;
  /**
   * エアブラシの噴霧密度 (density, 0..1) — `pen==='airbrush'` のときだけスライダを出す
   * (design §1.3 / §6)。`pen.airbrushDensity` を渡す。
   */
  readonly airbrushDensity: number;
  /** density スライダ変更で発火。呼び出し側が pen.airbrushDensity を書く。 */
  readonly onAirbrushDensityChange: (density: number) => void;
  /**
   * クレヨンの目の粗さ (grain, 0..2) — `pen==='crayon'` のときだけ segmented を出す
   * (phase11-light-pens-2026-08-21.md §2.6 / §6)。`pen.crayonGrain` を渡す。
   */
  readonly crayonGrain: number;
  /** grain 変更で発火。呼び出し側が pen.crayonGrain を書く。 */
  readonly onCrayonGrainChange: (grain: number) => void;
  /**
   * パステルの目の粗さ (grain, 0..2) — `pen==='pastel'` のときだけ segmented を出す
   * (同 §2.6 / §6)。`pen.pastelGrain` を渡す。
   */
  readonly pastelGrain: number;
  /** grain 変更で発火。呼び出し側が pen.pastelGrain を書く。 */
  readonly onPastelGrainChange: (grain: number) => void;
  /**
   * トーンの番手 (density) / 間隔 (pitch) — `pen==='tone'` のときだけ `PenParamsSection`
   * が内部でスライダ + segmented を出す (phase11-light-pens-2026-08-21.md §4.5-§4.7、
   * 封印解除)。
   */
  readonly toneDensity: number;
  readonly onToneDensityChange: (density: number) => void;
  readonly tonePitch: number;
  readonly onTonePitchChange: (pitch: number) => void;
  /**
   * ドットの間隔 (spacing) — `pen==='dot'` のときだけ `PenParamsSection` が内部で
   * スライダを出す (同 §3.4/§3.5/§6)。
   */
  readonly dotSpacing: number;
  readonly onDotSpacingChange: (spacing: number) => void;
}

/**
 * ペン種チップ群 + `PenParamsSection` (ペン別パラメータ)。
 * 余白等は最小に保ち、親 (Modern fieldset / Classic フロート) 側で配置する。
 */
export const PenKindPicker = ({
  pen,
  onSelectPen,
  texturePreset,
  onSelectTexturePreset,
  airbrushGrain,
  onAirbrushGrainChange,
  airbrushDensity,
  onAirbrushDensityChange,
  crayonGrain,
  onCrayonGrainChange,
  pastelGrain,
  onPastelGrainChange,
  toneDensity,
  onToneDensityChange,
  tonePitch,
  onTonePitchChange,
  dotSpacing,
  onDotSpacingChange,
}: PenKindPickerProps): React.ReactNode => (
  <div className="flex flex-col gap-3">
    {/* ペン種プリセット chips。 */}
    <fieldset className="m-0 flex flex-wrap gap-1 border-0 p-0" aria-label="ペン種">
      {MODERN_BRUSH_SUBTOOLS.map((sub) => (
        <button
          key={sub.pen}
          type="button"
          aria-pressed={pen === sub.pen}
          onClick={() => onSelectPen(sub.pen)}
          className={chipClass(pen === sub.pen)}
        >
          {sub.label}
        </button>
      ))}
    </fieldset>

    {/* ペン別パラメータ (texture のとき preset サムネ / airbrush のとき grain・density
        スライダ)。共有 `PenParamsSection` に集約済 (§8 / §9-1、見た目・挙動は抽出前と
        不変)。`PenKindPicker` は Classic (`PenPopup`) 専用に確定済み (Modern は
        `ModernToolProperties` が `PenParamsSection` を直接呼ぶ) なので、
        `showTexturePreset` は常に `true` 固定でよい (= Classic の preset サムネは従来
        どおり出続ける、2026-08-17 追記)。 */}
    <PenParamsSection
      pen={pen}
      showTexturePreset
      texturePreset={texturePreset}
      onSelectTexturePreset={onSelectTexturePreset}
      airbrushGrain={airbrushGrain}
      onAirbrushGrainChange={onAirbrushGrainChange}
      airbrushDensity={airbrushDensity}
      onAirbrushDensityChange={onAirbrushDensityChange}
      crayonGrain={crayonGrain}
      onCrayonGrainChange={onCrayonGrainChange}
      pastelGrain={pastelGrain}
      onPastelGrainChange={onPastelGrainChange}
      toneDensity={toneDensity}
      onToneDensityChange={onToneDensityChange}
      tonePitch={tonePitch}
      onTonePitchChange={onTonePitchChange}
      dotSpacing={dotSpacing}
      onDotSpacingChange={onDotSpacingChange}
    />
  </div>
);
